/* eslint-disable no-console */
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { FaChevronLeft, FaChevronRight, FaDownload, FaTh, FaImage, FaPlay, FaPause, FaExpand } from "react-icons/fa";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import { USERS_ALL_VENTAS } from "../../constants/permisos";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import {
  getOportunidades,
  getEdificiosFull,
  getMediaByClave,
  getReportesByClaves,
  getClientes,
  getContratos,
  getUsuarioClientes,
  construirUnidadesReportables,
  unidadId,
} from "../../utils";
import { Header } from "../Header";
import EmbudoVentaSelector, { periodoLabel } from "./EmbudoVentaSelector";
import PdfReporteFotografico from "./PdfReporteFotografico";
import { pdf } from "@react-pdf/renderer";

const ArrowBtn = ({ direction, onClick }) => (
  <button
    onClick={onClick}
    style={{
      position: "absolute",
      top: "50%",
      [direction === "left" ? "left" : "right"]: 40,
      transform: "translateY(-50%)",
      zIndex: 2,
      background: "rgba(0,0,0,0.5)",
      color: "#fff",
      border: "none",
      borderRadius: "50%",
      width: 40,
      height: 40,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    }}
  >
    {direction === "left" ? <FaChevronLeft /> : <FaChevronRight />}
  </button>
);

// "s" → "M:SS" (0 si no es finito). Para el tiempo del reproductor propio.
const fmtTime = (s) => {
  const t = Number.isFinite(s) && s > 0 ? s : 0;
  const m = Math.floor(t / 60);
  const sec = Math.floor(t % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

// Reproductor de video con controles PROPIOS y SIN control de volumen. Los controles
// nativos de <video controls> exponen un botón de volumen que no se puede ocultar por
// API estándar; acá el <video> va `muted` + `playsInline` sin `controls`, y la barra
// propia trae solo play/pausa, seek+tiempo y fullscreen. `fill` decide el wrapper:
// grilla llena la card (block, width 100%); slideshow abraza el video (inline-block).
// Único reproductor, usado por grilla y slideshow vía MediaReporte (regla 10.1).
const VideoReporte = ({ src, style, fill = false, poster }) => {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  };

  const handleSeek = (e) => {
    const v = videoRef.current;
    if (v) v.currentTime = Number(e.target.value);
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
    } else {
      const el = wrapRef.current;
      if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {});
    }
  };

  const iconBtn = {
    background: "transparent",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    padding: 2,
    display: "flex",
    alignItems: "center",
    fontSize: "0.8rem",
  };
  const timeText = { color: "#fff", fontSize: "0.65rem", fontVariantNumeric: "tabular-nums", flexShrink: 0 };

  return (
    <div
      ref={wrapRef}
      style={{
        position: "relative",
        background: "#000",
        lineHeight: 0,
        ...(fill ? { display: "block", width: "100%" } : { display: "inline-block", maxWidth: "100%" }),
      }}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        muted
        playsInline
        preload="metadata"
        style={{ display: "block", ...style }}
        onClick={togglePlay}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={() => setCurrent(videoRef.current?.currentTime || 0)}
        onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
      />
      {/* Barra propia — sin volumen. stopPropagation para no disparar el swipe del slider. */}
      <div
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 8px",
          background: "linear-gradient(transparent, rgba(0,0,0,0.65))",
        }}
      >
        <button type="button" style={iconBtn} onClick={togglePlay} title={playing ? "Pausar" : "Reproducir"}>
          {playing ? <FaPause /> : <FaPlay />}
        </button>
        <span style={timeText}>{fmtTime(current)}</span>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step="0.1"
          value={current}
          onChange={handleSeek}
          style={{ flex: 1, minWidth: 0, accentColor: "#2ce67c", cursor: "pointer" }}
        />
        <span style={timeText}>{fmtTime(duration)}</span>
        <button type="button" style={iconBtn} onClick={toggleFullscreen} title="Pantalla completa">
          <FaExpand />
        </button>
      </div>
    </div>
  );
};

// Render de un media del reporte (foto o video) con el tamaño que le pasa cada modo.
// Espeja el ramificado por tipo de la vista interna (regla 10.1): video → reproductor
// propio (VideoReporte, sin volumen), foto → <img>. Único punto de ramificación por
// tipo, usado por grilla y slideshow para no dejar dos ramas divergentes. `videoFill`
// pasa al wrapper del video (grilla llena la card; slideshow lo abraza).
const MediaReporte = ({ media, style, alt, onImgClick, videoFill = false }) =>
  media.tipo === "video" ? (
    <VideoReporte src={media.url} style={style} fill={videoFill} poster={media.poster_url || undefined} />
  ) : (
    <img src={media.url} alt={alt} style={style} onClick={onImgClick} />
  );

const ReporteFotograficoExterno = () => {
  const { pais, id: userId, rol, clienteId } = useAuthContext();

  // Unidades reportables: ventas directas + cuotas de contrato, en un carril único.
  const [unidades, setUnidades] = useState([]);
  const [edificiosMap, setEdificiosMap] = useState({});
  const [clientesById, setClientesById] = useState({});
  // Fuente de verdad única del selector (embudo controlado): { empresa, productoKey, saleId }.
  const [selection, setSelection] = useState({ empresa: "", productoKey: "", saleId: "" });
  const selectedUnidadId = selection.saleId;
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFotos, setLoadingFotos] = useState(false);
  const [unidadesConReporte, setUnidadesConReporte] = useState([]);
  const [edificioImages, setEdificioImages] = useState({});
  const [currentSlide, setCurrentSlide] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "slideshow" — externa abre en grilla
  // Empty-states de scope (rol scopeado sin acceso a ningún cliente): distinguir "no tenés
  // clientes vinculados" (contactá a Wecast) de un fallo al leer los vínculos (10.177).
  const [scopeVacio, setScopeVacio] = useState(false);
  const [scopeError, setScopeError] = useState(false);

  // Cargar unidades (ventas + cuotas de contrato) y edificios
  useEffect(() => {
    if (!pais || pais === "Region") return;
    const currentYear = new Date().getFullYear();

    const loadData = async () => {
      try {
        const [salesData, edData, clientesData, contratosData] = await Promise.all([
          getOportunidades({ pais, ano: currentYear }).catch(() => []),
          getEdificiosFull(),
          getClientes({ pais }).catch(() => []),
          getContratos({ pais }).catch(() => []),
        ]);

        const allSales = salesData || [];

        // Mapa de clientes por ID para resolver empresa/producto de cada unidad y del selector
        const clientesMap = (clientesData || []).reduce((acc, c) => ({ ...acc, [c.id]: c }), {});
        setClientesById(clientesMap);

        // Ventas: cerradas (100%) + con edificios (gate sin cambios). Contratos: 100% + no eliminada.
        const ventasFiltradas = allSales
          .filter((s) => s.estatus === "100%" && s.edificiosparticipantes && s.edificiosparticipantes.trim() !== "")
          .sort((a, b) => (b.id || 0) - (a.id || 0));
        const contratosFiltrados = (contratosData || []).filter((c) => c.estatus === "100%" && c.eliminada !== true);

        // Carril único. Gates de cuota (año del periodo + edificios asignados) van en el helper.
        const unidadesTodas = construirUnidadesReportables(ventasFiltradas, contratosFiltrados, clientesMap, {
          ano: currentYear,
        });

        // Filtro por rol sobre las unidades (venta y cuota por igual; ambas tienen client_id).
        // Roles scopeados (agencia/cliente): un contacto ve SOLO los clientes que tenga
        // vinculados. Fase 2 del modelo multi-cliente: el scope sale del set permitido
        // = cliente_id escalar (compat rol cliente + los 2 GM) ∪ vínculos de usuarios_clientes
        // (getUsuarioClientes, Fase 1). SIN fallback de cartera por agencia. Sin vínculos → cero.
        const esRolScopeado = rol === ROLES.AGENCIA || rol === ROLES.CLIENTE;

        let unidadesRol = unidadesTodas;
        let vacio = false;
        let errorVinculos = false;

        if (USERS_ALL_VENTAS.includes(userId)) {
          // Sin filtro — ven todo. No dependen de vínculos.
        } else if (esRolScopeado) {
          // Vínculos multi-cliente por query dedicada — NO se toca auth.js (10.49).
          const { data: vinculosIds, error: vinculosErr } = await getUsuarioClientes(userId);
          if (vinculosErr) {
            // No enmascarar la falla como "sin vínculos" (10.170 / 10.177).
            console.error("Error cargando clientes vinculados:", vinculosErr);
            errorVinculos = true;
          }

          // Set de clientes permitidos por ID (10.7): escalar ∪ vínculos.
          const clientesPermitidos = new Set([
            ...(clienteId ? [Number(clienteId)] : []),
            ...(vinculosIds || []).map(Number),
          ]);

          unidadesRol = unidadesTodas.filter((u) => u.client_id && clientesPermitidos.has(Number(u.client_id)));

          // Distinguir "sin clientes vinculados" (mensaje contactá a Wecast) de un fallo de
          // lectura que dejó el set vacío (mensaje de error). Si el escalar salvó el set (no
          // quedó vacío), no hay ni vacío ni error aunque el puente haya fallado.
          if (clientesPermitidos.size === 0) {
            vacio = !errorVinculos; // set vacío por dato real (no por fallo) → contactá a Wecast
          } else {
            errorVinculos = false;
          }
        }

        setScopeVacio(vacio);
        setScopeError(errorVinculos);
        setUnidades(unidadesRol);

        if (edData?.length > 0) {
          setEdificiosMap(edData.reduce((acc, e) => ({ ...acc, [e.id]: e }), {}));
        }
      } catch (error) {
        console.error("Error cargando datos:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [pais, rol, userId, clienteId]);

  // Cargar qué unidades ya tienen reportes (por clave: venta o cuota).
  useEffect(() => {
    if (unidades.length === 0) return;
    const loadReportes = async () => {
      try {
        const filas = await getReportesByClaves(unidades.map((u) => u.clave));
        setUnidadesConReporte([...new Set(filas.map((r) => unidadId(r)))]);
      } catch (error) {
        console.error("Error cargando reportes:", error);
      }
    };
    loadReportes();
  }, [unidades]);

  const unidadesVisibles = useMemo(
    () => unidades.filter((u) => unidadesConReporte.includes(u.id)),
    [unidades, unidadesConReporte]
  );

  const selectedUnidad = useMemo(() => unidades.find((u) => u.id === selectedUnidadId), [unidades, selectedUnidadId]);

  // Cargar media cuando se selecciona una unidad
  useEffect(() => {
    if (!selectedUnidad) {
      setFotos([]);
      return;
    }
    const loadFotos = async () => {
      setLoadingFotos(true);
      try {
        const data = await getMediaByClave(selectedUnidad.clave);
        setFotos(data);
      } catch (error) {
        console.error("Error cargando fotos:", error);
      } finally {
        setLoadingFotos(false);
      }
    };
    loadFotos();
  }, [selectedUnidad]);

  // Edificios de la unidad (venta: edificiosparticipantes; cuota: meses[].edificios_ids).
  const edificiosUnidad = useMemo(() => {
    if (!selectedUnidad?.fuenteEdificios) return [];
    const ids = selectedUnidad.fuenteEdificios
      .split(",")
      .map((s) => Number(s.trim()))
      .filter(Boolean);
    return ids
      .map((id) => edificiosMap[id])
      .filter(Boolean)
      .sort((a, b) => (a.edificio || "").localeCompare(b.edificio || ""));
  }, [selectedUnidad, edificiosMap]);

  // Solo edificios que tienen foto subida
  const slides = useMemo(() => {
    const result = [];
    fotos.forEach((foto) => {
      const edificio = edificiosMap[foto.idedificio];
      if (edificio) {
        result.push({ foto, edificio });
      }
    });
    return result;
  }, [fotos, edificiosMap]);

  // Cargar imágenes de edificios (foto exterior)
  const loadEdificioImage = useCallback(
    async (edificioId) => {
      if (edificioImages[edificioId] !== undefined) return;
      try {
        const src = await import(`../../img/edificios/${edificioId}.png`).catch(
          () => import(`../../img/edificios/${edificioId}.jpeg`)
        );
        setEdificioImages((prev) => ({ ...prev, [edificioId]: src.default }));
      } catch {
        setEdificioImages((prev) => ({ ...prev, [edificioId]: null }));
      }
    },
    [edificioImages]
  );

  useEffect(() => {
    slides.forEach((s) => loadEdificioImage(s.edificio.id));
  }, [slides, loadEdificioImage]);

  const handleDownloadPdf = async () => {
    if (!selectedUnidad || slides.length === 0) return;
    setPdfLoading(true);
    try {
      const doc = (
        <PdfReporteFotografico slides={slides} selectedSale={selectedUnidad} edificioImages={edificioImages} />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Nombre por cuota: incluir el mes (MES-AA) para no colisionar entre periodos del mismo cliente/campaña.
      const periodo = periodoLabel(selectedUnidad.mes_factura).replace("/", "-");
      link.download = `Reporte-${selectedUnidad.cliente}${selectedUnidad.campana ? `-${selectedUnidad.campana}` : ""}${
        periodo ? `-${periodo}` : ""
      }.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generando PDF:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  const sliderSettings = {
    dots: false,
    infinite: slides.length > 1,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    adaptiveHeight: false,
    beforeChange: (_, next) => setCurrentSlide(next),
  };

  const [sliderRef, setSliderRef] = useState(null);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <div style={{ padding: "15px 15px 0", flexShrink: 0 }}>
        <Header
          centerContent={
            selectedUnidad && (
              <div className="d-flex align-items-center gap-4 flex-wrap" style={{ fontSize: "0.95rem" }}>
                <span>
                  <span className="fw-semibold">Cliente </span>
                  <span>{selectedUnidad.cliente}</span>
                </span>
                {selectedUnidad.campana && (
                  <span>
                    <span className="fw-semibold">Campa&ntilde;a </span>
                    <span>{selectedUnidad.campana}</span>
                  </span>
                )}
                <span>
                  <span className="fw-semibold">Edificios </span>
                  <span>{edificiosUnidad.length}</span>
                </span>
                {selectedUnidad.periodopauta && (
                  <span>
                    <span className="fw-semibold">Per&iacute;odo </span>
                    <span>{selectedUnidad.periodopauta}d</span>
                  </span>
                )}
                {slides.length > 1 && (
                  <span>
                    <span className="fw-semibold">Foto </span>
                    <span>
                      {currentSlide + 1} de {slides.length}
                    </span>
                  </span>
                )}
              </div>
            )
          }
        >
          <div className="d-flex align-items-center gap-2">
            {slides.length > 0 && (
              <div className="btn-group btn-group-sm" role="group">
                <button
                  className={`btn ${viewMode === "slideshow" ? "btn-dark" : "btn-outline-dark"}`}
                  onClick={() => setViewMode("slideshow")}
                  title="Vista individual"
                >
                  <FaImage />
                </button>
                <button
                  className={`btn ${viewMode === "grid" ? "btn-dark" : "btn-outline-dark"}`}
                  onClick={() => setViewMode("grid")}
                  title="Vista grilla"
                >
                  <FaTh />
                </button>
              </div>
            )}
            {slides.length > 0 && (
              <button
                className="btn btn-outline-dark btn-sm"
                onClick={handleDownloadPdf}
                disabled={pdfLoading}
                title="Descargar PDF"
              >
                {pdfLoading ? <span className="spinner-border spinner-border-sm" role="status" /> : <FaDownload />}
              </button>
            )}
            {unidadesVisibles.length > 0 && (
              <EmbudoVentaSelector
                compact
                ventas={unidadesVisibles}
                clientesById={clientesById}
                selectedEmpresa={selection.empresa}
                selectedProductoKey={selection.productoKey}
                selectedSaleId={selectedUnidadId}
                onEmpresaChange={(empresa) => setSelection({ empresa, productoKey: "", saleId: "" })}
                onProductoChange={(productoKey) => setSelection((s) => ({ ...s, productoKey, saleId: "" }))}
                onSelectSale={(saleId) => setSelection((s) => ({ ...s, saleId }))}
              />
            )}
          </div>
        </Header>
      </div>

      {/* Área principal */}
      <div style={{ flex: 1, minHeight: 0, background: "#f8f9fa" }}>
        {!selectedUnidadId ? (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 px-4 text-center">
            {unidadesVisibles.length === 0 ? (
              scopeError ? (
                <p className="text-muted fs-5">
                  No pudimos cargar los clientes de tu cuenta. Reintent&aacute; m&aacute;s tarde o contact&aacute; a
                  Wecast.
                </p>
              ) : scopeVacio ? (
                <p className="text-muted fs-5">
                  Tu cuenta no tiene clientes vinculados. Contact&aacute; a Wecast para vincular tu cuenta.
                </p>
              ) : (
                <p className="text-muted fs-5">No hay reportes fotogr&aacute;ficos disponibles.</p>
              )
            ) : (
              <p className="text-muted" style={{ fontSize: "1.1rem" }}>
                Seleccione un reporte para visualizar
              </p>
            )}
          </div>
        ) : loadingFotos ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            <div className="spinner-border text-primary me-2" role="status" />
            <span className="text-muted">Cargando reporte...</span>
          </div>
        ) : slides.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            <span className="text-muted">A&uacute;n no hay fotos cargadas en este reporte.</span>
          </div>
        ) : viewMode === "grid" ? (
          <div style={{ height: "100%", overflow: "auto", padding: "16px 24px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 14,
              }}
            >
              {slides.map(({ foto, edificio }) => (
                <div
                  key={foto.id}
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: 8,
                    overflow: "hidden",
                    background: "#fff",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  }}
                >
                  <div style={{ padding: "8px 10px", lineHeight: 1.3 }}>
                    <div
                      className="fw-semibold"
                      style={{
                        fontSize: "0.75rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {edificio.edificio}
                    </div>
                    {edificio.direccion && (
                      <div
                        className="text-muted"
                        style={{
                          fontSize: "0.65rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {edificio.direccion}
                      </div>
                    )}
                  </div>
                  <MediaReporte
                    media={foto}
                    alt={edificio.edificio}
                    videoFill
                    style={{
                      width: "100%",
                      height: 150,
                      objectFit: "cover",
                      cursor: foto.tipo === "video" ? "default" : "pointer",
                    }}
                    onImgClick={() => window.open(foto.url, "_blank")}
                  />
                  {foto.tipo === "video" && (
                    <div style={{ padding: "4px 10px 8px" }}>
                      <a href={foto.url} target="_blank" rel="noreferrer" style={{ fontSize: "0.65rem" }}>
                        Abrir video en pesta&ntilde;a
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ height: "100%", position: "relative" }}>
            <Slider ref={setSliderRef} {...sliderSettings}>
              {slides.map(({ foto, edificio }) => {
                const edImg = edificioImages[edificio.id];
                const totalPantallas = (edificio.pantallas || 0) + (edificio.totems || 0) + (edificio.espera || 0);

                return (
                  <div key={foto.id}>
                    <div
                      style={{
                        height: "calc(100vh - 52px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "#f8f9fa",
                        padding: "10px 60px 50px",
                      }}
                    >
                      {/* Bloque foto + ficha juntos, centrados */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 24,
                          alignItems: "stretch",
                          maxHeight: 720,
                        }}
                      >
                        {/* Foto principal */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <MediaReporte
                            media={foto}
                            alt={edificio.edificio}
                            style={{
                              maxWidth: "100%",
                              maxHeight: 720,
                              objectFit: "contain",
                              borderRadius: 10,
                              border: "1px solid #dee2e6",
                            }}
                          />
                        </div>

                        {/* Ficha técnica - mismo alto que la foto */}
                        <div
                          style={{
                            width: 320,
                            flexShrink: 0,
                          }}
                        >
                          <div
                            style={{
                              border: "1px solid #dee2e6",
                              borderRadius: 12,
                              overflow: "hidden",
                              background: "#fff",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                              display: "flex",
                              flexDirection: "column",
                              height: "100%",
                            }}
                          >
                            {/* Barra verde con nombre */}
                            <div
                              style={{
                                background: "#2ce67c",
                                padding: "10px 10px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "1rem",
                                  fontWeight: 700,
                                  color: "#000",
                                  lineHeight: 1.2,
                                  fontFamily: "'Trebuchet MS', sans-serif",
                                }}
                              >
                                {edificio.edificio}
                              </div>
                            </div>

                            {/* Datos del edificio */}
                            <div
                              style={{
                                padding: "10px 10px",
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                              }}
                            >
                              {edificio.direccion && (
                                <div
                                  style={{
                                    textAlign: "center",
                                    fontSize: "0.75rem",
                                    color: "#6c757d",
                                  }}
                                >
                                  {edificio.direccion}
                                  {edificio.ubicacion ? `, ${edificio.ubicacion}` : ""}
                                </div>
                              )}

                              {(edificio.personas || totalPantallas > 0) && (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: 28,
                                    padding: "10px 0",
                                    borderTop: "1px solid #eee",
                                    borderBottom: "1px solid #eee",
                                  }}
                                >
                                  {edificio.personas && (
                                    <div style={{ textAlign: "center" }}>
                                      <div
                                        style={{
                                          fontSize: "1.6rem",
                                          fontWeight: 700,
                                          color: "#2ce67c",
                                          lineHeight: 1.1,
                                        }}
                                      >
                                        {Number(edificio.personas).toLocaleString("es-CL")}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "0.65rem",
                                          textTransform: "uppercase",
                                          color: "#999",
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        Personas
                                      </div>
                                    </div>
                                  )}
                                  {totalPantallas > 0 && (
                                    <div style={{ textAlign: "center" }}>
                                      <div
                                        style={{
                                          fontSize: "1.6rem",
                                          fontWeight: 700,
                                          color: "#2ce67c",
                                          lineHeight: 1.1,
                                        }}
                                      >
                                        {totalPantallas}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "0.65rem",
                                          textTransform: "uppercase",
                                          color: "#999",
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        Pantallas
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Imagen exterior del edificio */}
                            {edImg && (
                              <div style={{ flex: 1, minHeight: 120, overflow: "hidden" }}>
                                <img
                                  src={edImg}
                                  alt={edificio.edificio}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Slider>

            {/* Flechas de navegación */}
            {slides.length > 1 && (
              <>
                <ArrowBtn direction="left" onClick={() => sliderRef?.slickPrev()} />
                <ArrowBtn direction="right" onClick={() => sliderRef?.slickNext()} />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReporteFotograficoExterno;
